/**
 * React Query hooks for issue comments data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listIssueComments,
  createIssueComment,
  updateIssueComment,
  deleteIssueComment,
  type CreateCommentData,
  type UpdateCommentData,
} from "@/services/issueCommentsService";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch a paginated list of comments for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param page - The page number (default: 1)
 */
export function useIssueComments(issueId: number, page: number = 1) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["issue-comments", issueId, page],
    queryFn: () => listIssueComments(issueId, page),
    staleTime: 30000, // 30 seconds
    enabled: issueId > 0 && isApiReady,
  });
}

/**
 * Hook to create a new comment on a security issue.
 * Invalidates the comments list and activities list on settled.
 */
export function useCreateIssueComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: number; data: CreateCommentData }) =>
      createIssueComment(issueId, data),
    onSettled: (_data, error, variables) => {
      if (error) {
        toast.error("Failed to add comment");
      } else {
        toast.success("Comment added");
      }
      // Invalidate comments for this issue
      queryClient.invalidateQueries({ queryKey: ["issue-comments", variables.issueId] });
      // Invalidate activities so the History tab picks up the new COMMENT_ADDED entry
      queryClient.invalidateQueries({ queryKey: ["issue-activities"] });
    },
  });
}

/**
 * Hook to update a comment on a security issue.
 * Invalidates the comments list and activities list on settled.
 */
export function useUpdateIssueComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      issueId,
      commentId,
      data,
    }: {
      issueId: number;
      commentId: number;
      data: UpdateCommentData;
    }) => updateIssueComment(issueId, commentId, data),
    onSettled: (_data, error, variables) => {
      if (error) {
        toast.error("Failed to update comment");
      } else {
        toast.success("Comment updated");
      }
      queryClient.invalidateQueries({ queryKey: ["issue-comments", variables.issueId] });
      queryClient.invalidateQueries({ queryKey: ["issue-activities"] });
    },
  });
}

/**
 * Hook to delete (soft-delete) a comment on a security issue.
 * Invalidates the comments list on settled.
 */
export function useDeleteIssueComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, commentId }: { issueId: number; commentId: number }) =>
      deleteIssueComment(issueId, commentId),
    onSettled: (_data, error, variables) => {
      if (error) {
        toast.error("Failed to delete comment");
      } else {
        toast.success("Comment deleted");
      }
      queryClient.invalidateQueries({ queryKey: ["issue-comments", variables.issueId] });
    },
  });
}
